import { useCallback, useEffect, useState } from "react"

import { getTemplates } from "../../../DocumentTemplates/api/services.ts"
import { getDashboardTemplates } from "../../../SettingsDashboardTemplates/api/services.ts"
import { asTemplateList } from "../../../SettingsDashboardTemplates/helpers/payload.ts"
import { type PermissionSettingsPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { getProjectTypes } from "../../../Templates/services/project-types.services.ts"

export type TemplateCountKey = "project" | "documents" | "dashboard"

export type TemplateCountState =
  | { status: "loading" }
  | { status: "ready"; count: number }
  | { status: "unavailable" }
  | { status: "error" }

export type TemplateCounts = Record<TemplateCountKey, TemplateCountState>

const LOADING: TemplateCountState = { status: "loading" }
const UNAVAILABLE: TemplateCountState = { status: "unavailable" }
const ERROR: TemplateCountState = { status: "error" }

// 404 means the type isn't enabled for this workspace; anything else is a
// real failure we should surface (and let the user retry) rather than hide.
const failure = (status: number | undefined): TemplateCountState =>
  status === 404 ? UNAVAILABLE : ERROR

export function useTemplateCounts(
  permissions: PermissionSettingsPermissions | undefined
) {
  const canViewProject =
    !!permissions?.project_templates?.settings_template_view
  const canViewDocuments =
    !!permissions?.document_templates?.settings_proposal_template_view
  const canViewDashboard = !!permissions?.permissions?.settings_permissions_view

  const [reloadToken, setReloadToken] = useState(0)
  const [counts, setCounts] = useState<TemplateCounts>({
    project: LOADING,
    documents: LOADING,
    dashboard: LOADING
  })

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  useEffect(() => {
    let active = true

    const apply = (key: TemplateCountKey, state: TemplateCountState) => {
      if (active) setCounts((prev) => ({ ...prev, [key]: state }))
    }

    setCounts({
      project: canViewProject ? LOADING : UNAVAILABLE,
      documents: canViewDocuments ? LOADING : UNAVAILABLE,
      dashboard: canViewDashboard ? LOADING : UNAVAILABLE
    })

    if (canViewProject) {
      void getProjectTypes({ page_size: 1 })
        .then((res) =>
          apply(
            "project",
            res.ok
              ? { status: "ready", count: res.data?.count ?? 0 }
              : failure(res.status)
          )
        )
        .catch(() => apply("project", ERROR))
    }

    if (canViewDocuments) {
      void getTemplates({ page_size: 1 })
        .then((res) =>
          apply(
            "documents",
            res.ok
              ? { status: "ready", count: res.data?.count ?? 0 }
              : failure(res.status)
          )
        )
        .catch(() => apply("documents", ERROR))
    }

    if (canViewDashboard) {
      void getDashboardTemplates()
        .then((res) =>
          apply(
            "dashboard",
            res.ok
              ? { status: "ready", count: asTemplateList(res.data).length }
              : failure(res.status)
          )
        )
        .catch(() => apply("dashboard", ERROR))
    }

    return () => {
      active = false
    }
  }, [canViewProject, canViewDocuments, canViewDashboard, reloadToken])

  return { counts, reload }
}
